import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FacetBlock } from '@/features/shared/components/Facet';
import { arr } from '@/features/shared/lib/utils';
import noUiSlider from 'nouislider';
import '@/styles/nouislider.min.css';

export default function PublicationsFilterSidebar({
  facet,
  filters,
  setFilters,
  pendingFilters,
  setPendingFilters,
  conjunctions,
  setConjunctions,
  sort,
  query,
  setQuery,
  doSearch,
  syncUrl,
  fetchList,
  cleanFilters,
  setPopup,
  setGraphExpanded,
}) {
  // Debounce timer for search input
  const typingTimerRef = useRef(null);
  useEffect(() => () => { if (typingTimerRef.current) clearTimeout(typingTimerRef.current); }, []);
  const DEBOUNCE_MS = 500;
  const runSearchNow = () => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    if (typeof doSearch === 'function') doSearch();
  };
  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    if (typeof setQuery === 'function') setQuery(val);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      runSearchNow();
    }, DEBOUNCE_MS);
  };

  // Publication Year 탭 상태
  const [yearTab, setYearTab] = useState('range'); // 'range' | 'individual'
  const [yearRange, setYearRange] = useState([null, null]); // [min, max]
  const [yearRangePending, setYearRangePending] = useState([null, null]);
  const sliderRef = useRef(null);
  const sliderInstanceRef = useRef(null);
  const minInputRef = useRef(null);
  const maxInputRef = useRef(null);
  const svgPathRef = useRef(null);

  // publish_year 패싯 데이터
  const publishYearFacet = useMemo(() => {
    const yearData = facet?.publish_year;
    if (!yearData || !Array.isArray(yearData.result)) return [];
    return yearData.result
      .map(item => ({
        title: String(item.name || '').trim(),
        count: Number(item.count || 0)
      }))
      .filter(item => item.title && !isNaN(Number(item.title)))
      .sort((a, b) => Number(b.title) - Number(a.title)); // 최신순
  }, [facet?.publish_year]);

  // 연도 범위 계산
  const yearMinMax = useMemo(() => {
    if (!publishYearFacet.length) return { min: 2000, max: new Date().getFullYear() };
    const years = publishYearFacet.map(item => Number(item.title)).filter(y => !isNaN(y));
    return {
      min: Math.min(...years),
      max: Math.max(...years)
    };
  }, [publishYearFacet]);

  // SVG 차트 경로 생성
  const generateSVGPath = useMemo(() => {
    if (!publishYearFacet.length || !yearMinMax) return 'M0,60 Q100,10 200,40 T400,60 L400,80 L0,80 Z';

    const width = 400;
    const height = 80;
    const padding = 20;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    const maxCount = Math.max(...publishYearFacet.map(item => item.count));
    const minCount = Math.min(...publishYearFacet.map(item => item.count));
    const countRange = maxCount - minCount || 1;

    // 모든 연도 범위를 채우기 위해 연도별 데이터 매핑
    const yearMap = new Map();
    publishYearFacet.forEach(item => {
      yearMap.set(Number(item.title), item.count);
    });

    // 최소~최대 연도 범위의 모든 연도에 대해 포인트 생성
    const points = [];
    for (let year = yearMinMax.min; year <= yearMinMax.max; year++) {
      const count = yearMap.get(year) || 0;
      const normalizedYear = (year - yearMinMax.min) / (yearMinMax.max - yearMinMax.min);
      const x = padding + normalizedYear * chartWidth;
      const normalizedCount = countRange > 0 ? (count - minCount) / countRange : 0;
      const y = height - padding - normalizedCount * chartHeight;
      points.push({ x, y, year, count });
    }

    if (points.length === 0) return 'M0,60 Q100,10 200,40 T400,60 L400,80 L0,80 Z';

    // 스무스 커브 경로 생성 (베지어 커브 사용)
    let path = `M${points[0].x},${points[0].y}`;

    if (points.length === 1) {
      path += ` L${points[0].x},${height} L${points[0].x},${height} Z`;
    } else {
      // 각 점 사이를 부드럽게 연결
      for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        const next = points[i + 1] || curr;

        // 제어점 계산 (스무스 커브)
        const dx = (next.x - prev.x) * 0.2;
        const cp1x = prev.x + dx;
        const cp1y = prev.y;
        const cp2x = curr.x - dx;
        const cp2y = curr.y;

        path += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
      }

      // 하단 닫기
      path += ` L${points[points.length - 1].x},${height} L${points[0].x},${height} Z`;
    }

    return path;
  }, [publishYearFacet, yearMinMax]);

  // 슬라이더 초기화
  useEffect(() => {
    if (yearTab !== 'range' || !sliderRef.current || !publishYearFacet.length) return;

    const slider = sliderRef.current;

    // 기존 슬라이더 제거
    if (sliderInstanceRef.current) {
      sliderInstanceRef.current.destroy();
    }

    // 새 슬라이더 생성
    const instance = noUiSlider.create(slider, {
      start: [yearMinMax.min, yearMinMax.max],
      connect: true,
      range: {
        min: yearMinMax.min,
        max: yearMinMax.max
      },
      step: 1,
      format: {
        to: (value) => Math.round(value),
        from: (value) => Number(value)
      }
    });

    sliderInstanceRef.current = instance;

    // 슬라이더 값 변경 시
    instance.on('update', (values) => {
      const [min, max] = values.map(v => Number(v));
      setYearRangePending([min, max]);
      if (minInputRef.current) minInputRef.current.value = min;
      if (maxInputRef.current) maxInputRef.current.value = max;
    });

    // 입력 필드 값 변경 시
    const handleMinInput = (e) => {
      const value = Number(e.target.value);
      if (!isNaN(value) && value >= yearMinMax.min && value <= yearMinMax.max) {
        instance.set([value, null]);
      }
    };

    const handleMaxInput = (e) => {
      const value = Number(e.target.value);
      if (!isNaN(value) && value >= yearMinMax.min && value <= yearMinMax.max) {
        instance.set([null, value]);
      }
    };

    if (minInputRef.current) {
      minInputRef.current.addEventListener('change', handleMinInput);
    }
    if (maxInputRef.current) {
      maxInputRef.current.addEventListener('change', handleMaxInput);
    }

    return () => {
      if (minInputRef.current) {
        minInputRef.current.removeEventListener('change', handleMinInput);
      }
      if (maxInputRef.current) {
        maxInputRef.current.removeEventListener('change', handleMaxInput);
      }
      if (instance) {
        instance.destroy();
      }
    };
  }, [yearTab, yearMinMax, publishYearFacet.length]);

  // SVG 경로 업데이트
  useEffect(() => {
    if (svgPathRef.current && generateSVGPath) {
      svgPathRef.current.setAttribute('d', generateSVGPath);
    }
  }, [generateSVGPath]);

  // Range 탭에서 Refine 클릭 시
  const handleYearRangeRefine = () => {
    const [min, max] = yearRangePending;
    if (!min || !max || min > max) return;

    // 범위 내 모든 연도 생성
    const years = [];
    for (let year = min; year <= max; year++) {
      years.push(String(year));
    }

    // publish_year 필터 업데이트 (독립적으로)
    const nextFilters = { ...filters };
    if (years.length > 0) {
      nextFilters.publish_year = years;
    } else {
      delete nextFilters.publish_year;
    }

    setFilters(nextFilters);
    setPendingFilters(nextFilters); // pendingFilters도 동기화
    setYearRange([min, max]);
    // 그래프 닫기
    if (setGraphExpanded) setGraphExpanded(false);

    // API 호출
    const extra = {};
    Object.entries(nextFilters).forEach(([k, vals]) => {
      if (Array.isArray(vals) && vals.length) {
        if (k === 'publish_year') {
          // publish_year는 배열로 전송
          extra[`${k}[]`] = vals;
        } else {
          extra[`${k}[]`] = vals;
        }
      }
    });
    Object.entries(conjunctions).forEach(([k, conj]) => {
      if (conj === 'and') {
        extra[`${k}_conjunction`] = 'and';
      }
    });
    if (sort?.order_by) extra.order_by = sort.order_by;
    if (sort?.order) extra.order = sort.order;
    if (String(query || '').trim()) extra.sub_ks = String(query).trim();

    syncUrl({ page: 1, filters: nextFilters, sort, conjunctions });
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    } catch (e) { }
    fetchList(1, { extra });
  };

  // Individual 탭에서 publish_year 필터 관리
  const handleYearIndividualToggle = (value) => {
    setPendingFilters(prev => {
      const prevArr = Array.isArray(prev.publish_year) ? prev.publish_year : [];
      const exists = prevArr.includes(value);
      const nextArr = exists ? prevArr.filter(v => v !== value) : [...prevArr, value];
      return { ...prev, publish_year: nextArr };
    });
  };

  const handleYearIndividualRefine = () => {
    // 현재 적용된 filters와 pendingFilters를 병합하여 모든 필터 유지
    const mergedFilters = { ...filters, ...pendingFilters };
    const next = cleanFilters(mergedFilters);
    setFilters(next);
    setPendingFilters(next);
    // 그래프 닫기
    if (setGraphExpanded) setGraphExpanded(false);
    const extra = {};
    Object.entries(next).forEach(([k, vals]) => {
      if (Array.isArray(vals) && vals.length) {
        if (k === 'publish_year') {
          extra[`${k}[]`] = vals;
        } else {
          extra[`${k}[]`] = vals;
        }
      }
    });
    Object.entries(conjunctions).forEach(([k, conj]) => {
      if (conj === 'and') {
        extra[`${k}_conjunction`] = 'and';
      }
    });
    if (sort?.order_by) extra.order_by = sort.order_by;
    if (sort?.order) extra.order = sort.order;
    if (String(query || '').trim()) extra.sub_ks = String(query).trim();
    syncUrl({ page: 1, filters: next, sort, conjunctions });
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    } catch (e) { }
    fetchList(1, { extra });
  };

  return (
    <div className="lg:w-[310px] lg:mt-[45px]">
      <div className="filter fixed top-[100%] right-0 z-[101] w-full h-full transition-top duration-300 overflow-y-auto px-6 py-[30px] bg-[#fff] overflow-y-auto lg:rounded-xl lg:static">
        <div className="filter-top flex items-center gap-[10px] relative lg:justify-between">
          <h3 className="text-[18px] font-bold">Publication Search</h3>
          <button
            className="btn-resetAll text-[13px] text-[#6C7687] font-semibold cursor-pointer"
            onClick={() => {
              setFilters({});
              setPendingFilters({});
              setConjunctions({});
              const extra = {};
              if (sort?.order_by) extra.order_by = sort.order_by;
              if (sort?.order) extra.order = sort.order;
              if (String(query || '').trim()) extra.sub_ks = String(query).trim();
              syncUrl({ page: 1, filters: {}, sort, conjunctions: {} });
              try {
                if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }
              } catch (e) {
              }
              fetchList(1, { extra });
            }}
          >
            Reset all
          </button>
          <button className="filter-close absolute top-0 right-0 w-[25px] h-[25px] cursor-pointer lg:hidden">
            <img
              src={`${rmVars.themeUrl}images/clear-icon.png`}
              className="w-[18px]"
              alt="close"
            />
          </button>
        </div>

        <div className="filter-search relative mt-[20px]">
          <input
            type="text"
            placeholder="Search within result..."
            value={query}
            onChange={handleSearchInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runSearchNow();
            }}
            className="w-full h-[46px] px-4 pr-[46px] border border-[#E4E8F1] outline-none rounded-lg text-[13px] text-[#333] placeholder:text-[#B4BCCC] hover:border-[#8D9AAF] focus:border-[#8D9AAF] disabled:border-[#E4E8F1] disabled:bg-[#F3F5F9] disabled:text-[#B4BCCC] disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={doSearch}
            className="absolute top-1/2 right-[18px] -translate-y-1/2 cursor-pointer"
          >
            <img
              src={`${rmVars.themeUrl}images/search-ico-8D9AAF.png`}
              className="w-[18px]"
              alt="search icon"
            />
          </button>
        </div>

        <div className="filter-cont mt-10">
          <div className="filter-item-wrap">
            <div className="filter-item mb-9">
              <h4 className="mb-[18px] text-[14px] font-bold">
                Publication Year
              </h4>

              <ul className="p-[6px] bg-[#F3F5F9] rounded-lg z-10 text-[#8D9AAF] flex text-[15px] gap-[5px] mb-[18px]">
                <li
                  className={`${yearTab === 'range' ? 'active bg-white ' : ''}w-[calc((100%-5px)/2)] text-center leading-[38px] hover:bg-white rounded-lg cursor-pointer transition-colors duration-200`}
                  onClick={() => setYearTab('range')}
                >
                  Range
                </li>
                <li
                  className={`${yearTab === 'individual' ? 'active bg-white ' : ''}w-[calc((100%-5px)/2)] text-center leading-[38px] hover:bg-white rounded-lg cursor-pointer transition-colors duration-200`}
                  onClick={() => setYearTab('individual')}
                >
                  Individual
                </li>
              </ul>

              {yearTab === 'range' ? (
                <div>
                  <div className="relative w-full mx-auto mt-20">
                    <svg viewBox="0 0 400 80" className="absolute top-[-80px] left-0 w-full h-[80px] z-0">
                      <path
                        ref={svgPathRef}
                        d={generateSVGPath}
                        fill="#4B4DFF1A"
                        stroke="none"
                      />
                    </svg>

                    <div ref={sliderRef} className="relative z-10"></div>

                    <div className="flex items-center gap-2 mt-6">
                      <input
                        ref={minInputRef}
                        type="number"
                        className="w-full h-[34px] px-2 border border-[#E4E8F1] rounded-lg text-[13px] text-[#333]"
                        placeholder={yearMinMax.min?.toString()}
                      />
                      <span className="text-[#8D9AAF]">~</span>
                      <input
                        ref={maxInputRef}
                        type="number"
                        className="w-full h-[36px] px-2 border border-[#E4E8F1] rounded-lg text-[13px] text-[#333]"
                        placeholder={yearMinMax.max?.toString()}
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleYearRangeRefine}
                    className="w-full flex items-center justify-center gap-[6px] py-[8px] mt-[8px] text-[14px] font-semibold border border-[#B4BCCC] rounded-md cursor-pointer"
                  >
                    <img
                      src={`${rmVars.themeUrl}images/search-ico-333.png`}
                      className="w-[12px]"
                      alt="search"
                    />
                    Refine
                  </button>
                </div>
              ) : (
                <div>
                  <ul className="flex flex-col gap-1">
                    {publishYearFacet.slice(0, 5).map((item, idx) => {
                      const checked = Array.isArray(pendingFilters.publish_year) && pendingFilters.publish_year.includes(item.title);
                      return (
                        <li key={`${item.title}-${idx}`} className="w-full">
                          <label className="checkbox-wrap group inline-flex items-start cursor-pointer select-none">
                            <input
                              type="checkbox"
                              className="peer hidden"
                              checked={checked}
                              onChange={() => handleYearIndividualToggle(item.title)}
                            />
                            <span className="w-4 h-4 shrink-0 flex items-center justify-center mt-[3px] border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200" />
                            <span className="ml-[10px] text-[14px] text-[#6C7687]">
                              {item.title} (<span>{item.count.toLocaleString()}</span>)
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>

                  {publishYearFacet.length > 5 && (
                    <button
                      onClick={() => setPopup({ open: true, key: 'publish_year', title: 'Publication Year' })}
                      className="btn-filter-more w-[calc(50%-8px)] flex items-center justify-center gap-[6px] py-[8px] text-[14px] font-semibold border border-[#B4BCCC] rounded-md cursor-pointer lg:w-auto lg:py-[2px] lg:border-none lg:px-[10px] lg:bg-[#F3F5F9] lg:text-[13px] lg:text-[#8D9AAF] lg:gap-[2px] mt-4"
                    >
                      <img src={`${rmVars.themeUrl}images/more-ico-bk.png`} className="w-[14px] lg:hidden" alt="more" />
                      <img src={`${rmVars.themeUrl}images/more-ico-gr.png`} className="w-[14px] hidden lg:block" alt="more" />
                      More
                    </button>
                  )}

                  <button
                    onClick={handleYearIndividualRefine}
                    className="w-full flex items-center justify-center gap-[6px] py-[8px] mt-[8px] text-[14px] font-semibold border border-[#B4BCCC] rounded-md cursor-pointer"
                  >
                    <img
                      src={`${rmVars.themeUrl}images/search-ico-333.png`}
                      className="w-[12px]"
                      alt="search"
                    />
                    Refine
                  </button>
                </div>
              )}
            </div>

            {Object.entries(facet)
              .filter(([key]) => key !== 'publish_year') // publish_year는 별도 섹션에서 처리
              .sort((a, b) => (a[0] === 'registered_db_name' ? -1 : (b[0] === 'registered_db_name' ? 1 : 0)))
              .map(([key, value]) => {
                const items = value?.result ? value : arr(value);
                if (Array.isArray(items) && !items.length) return null;
                if (items && typeof items === 'object' && !Array.isArray(items) && Array.isArray(items.result) && !items.result.length) return null;

                // key를 사람이 읽기 좋은 형태로 변환
                const title = value?.title || key
                  .split('_')
                  .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                  .join(' ');

                // 특정 facet만 옵션 활성화
                const isRegisteredDb = key === 'registered_db_name';

                // conjunction 필드 확인 (dept 등)
                const hasConjunction = value?.is_conjunction;
                const currentConjunction = hasConjunction ? (conjunctions[key] || null) : null;

                return (
                  <FacetBlock
                    key={key}
                    title={title}
                    items={items}
                    showAll={isRegisteredDb}
                    andQuery={isRegisteredDb}
                    facetKey={key}
                    selected={pendingFilters[key] || []}
                    conjunction={hasConjunction ? 'and' : null}
                    conjunctionValue={currentConjunction}
                    onConjunctionChange={(facetKey, conjunctionValue) => {
                      setConjunctions(prev => ({
                        ...prev,
                        [facetKey]: conjunctionValue
                      }));
                    }}
                    onToggle={(facetKey, value) => {
                      setPendingFilters(prev => {
                        const prevArr = Array.isArray(prev[facetKey]) ? prev[facetKey] : [];
                        const exists = prevArr.includes(value);
                        const nextArr = exists ? prevArr.filter(v => v !== value) : [...prevArr, value];
                        return { ...prev, [facetKey]: nextArr };
                      });
                    }}
                    onRefine={() => {
                      // 현재 적용된 filters와 pendingFilters를 병합하여 모든 필터 유지
                      const mergedFilters = { ...filters, ...pendingFilters };
                      const next = cleanFilters(mergedFilters);
                      setFilters(next);
                      setPendingFilters(next);
                      // 그래프 닫기
                      if (setGraphExpanded) setGraphExpanded(false);
                      const extra = {};
                      Object.entries(next).forEach(([k, vals]) => {
                        if (Array.isArray(vals) && vals.length) extra[`${k}[]`] = vals;
                      });
                      // conjunction 파라미터 추가
                      Object.entries(conjunctions).forEach(([k, conj]) => {
                        if (conj === 'and') {
                          extra[`${k}_conjunction`] = 'and';
                        }
                      });
                      if (sort?.order_by) extra.order_by = sort.order_by;
                      if (sort?.order) extra.order = sort.order;
                      if (String(query || '').trim()) extra.sub_ks = String(query).trim();
                      syncUrl({ page: 1, filters: next, sort, conjunctions });
                      try {
                        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                        }
                      } catch (e) {
                      }
                      fetchList(1, { extra });
                    }}
                    onMore={(params) => {
                      // FacetBlock은 facetKey로 키를 넘김 → Popup은 key를 사용함
                      setPopup({ open: true, key: params?.facetKey || params?.key || '', ...params });
                    }}
                  />
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
